import { occupationType, visaType } from 'apis/handler/auth/authSchema';
import { roomType } from 'apis/handler/listing/listingSchema';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { COUNTRY_CODE_OPTIONS } from 'shared/constants';

import stationData from './subway.json';
import { removeWhitespace } from './utility';

/**
 *
 * @param occupation 유저가 보는 occupation
 * @returns 서버에 보내는 occupation
 */
export const convertOccupationToRequest = (occupation: string): occupationType => {
  switch (occupation) {
    case 'Undergraduate Student':
      return 'UNDERGRADUATE';
    case 'Graduate Student':
      return 'GRADUATE';
    case 'Exchange Student':
      return 'EXCHANGE';
    case 'Education/Academic Research':
      return 'EDUCATION';
    case 'IT/Software/Engineering':
      return 'IT_SOFTWARE_ENGINEERING';
    case 'Manufacture/Agriculture':
      return 'MANUFACTURE_AGRICULTURE';
    case 'Arts/Design':
      return 'ARTS_DESIGN';
    case 'Finance/Banking':
      return 'FINANCE_BANKING';
    case 'Public Service/Government':
      return 'PUBLIC_SERVICE';
    case 'Healthcare/Medical Service':
      return 'HEALTHCARE_MEDICAL';
    case 'Business/Sales':
      return 'BUSINESS_SALES';
    case 'Legal/Consulting':
      return 'LEGAL_CONSULTING';
    case 'Others':
      return 'OTHERS';
    default:
      return 'OTHERS';
  }
};

/**
 *
 * @param occupation 서버에서 받는 occupation
 * @returns 유저가 보는 occupation
 */
export const convertRequestToOccupation = (occupationType: string): string => {
  switch (occupationType) {
    case 'UNDERGRADUATE':
      return 'Undergraduate Student';
    case 'GRADUATE':
      return 'Graduate Student';
    case 'EXCHANGE':
      return 'Exchange Student';
    case 'EDUCATION':
      return 'Education/Academic Research';
    case 'IT_SOFTWARE_ENGINEERING':
      return 'IT/Software/Engineering';
    case 'MANUFACTURE_AGRICULTURE':
      return 'Manufacture/Agriculture';
    case 'ARTS_DESIGN':
      return 'Arts/Design';
    case 'FINANCE_BANKING':
      return 'Finance/Banking';
    case 'PUBLIC_SERVICE':
      return 'Public Service/Government';
    case 'HEALTHCARE_MEDICAL':
      return 'Healthcare/Medical Service';
    case 'BUSINESS_SALES':
      return 'Business/Sales';
    case 'LEGAL_CONSULTING':
      return 'Legal/Consulting';
    case 'OTHERS':
      return 'Others';
    default:
      return 'null';
  }
};

/**
 *
 * @param visaInfo 유저가 보는 visaInfo
 * @returns 서버에 보내는 visaInfo
 */
export const convertVisaInfoToRequest = (visaInfo: string): visaType => {
  switch (visaInfo) {
    case 'Diplomatic/Official(A-1,A-2)':
      return 'DIPLOMATIC_OFFICIAL';
    case 'Visa Exempted(B)':
      return 'VISA_EXEMPTED';
    case 'Journalism/Religious Affairs(C-1, D-5, D-6)':
      return 'JOURNALISM';
    case 'Short Term Visit(C-2, C-3)':
      return 'SHORT_TEM_VISIT';
    case 'Study(D-2)':
      return 'STUDY';
    case 'Language Study(D-4)':
      return 'LANGUAGE_STUDY';
    case 'Technical Trainee(D-3)':
      return 'TECHNICAL_TRAINEE';
    // D-3·D-4 미구분 레거시 — 서버가 LANGUAGE_STUDY 로 저장한다
    case 'Trainee(D-3,D-4)':
      return 'TRAINEE';
    case 'Intra-Company Transfer(D-7)':
      return 'INTRA_COMPANY_TRANSFER';
    case 'Professional(C-4, D-1, D-8, D-9, D-10, E-1, E-2, E-3, E-4, E-5, E-6, E-7)':
      return 'PROFESSIONAL';
    case 'Non-Professional(E-8, E-9, E-10)':
      return 'NON_PROFESSIONAL';
    case 'Working Holiday(H-1)':
      return 'WORKING_HOLIDAY';
    case 'Work and Visit(H-2)':
      return 'WORK_VISIT';
    case 'Family Visitor/Dependent Family(F-1, F-2, F-3)':
      return 'FAMILY_VISITOR';
    case 'Overseas Korean(F-4)':
      return 'OVERSEA_KOREAN';
    case 'Permanent Residence(F-5)':
      return 'PERMANENT_RESIDENCE';
    case 'Marriage Migrant(F-6)':
      return 'MARRIAGE_MIGRANT';
    case 'Others(G-1)':
      return 'OTHERS';
    default:
      return 'OTHERS';
  }
};

/**
 *
 * @param visaType 서버에서 받는 visaType
 * @returns 유저가 보는 visaType
 */
export const convertRequestToVisaInfo = (visaType: string): string => {
  switch (visaType) {
    case 'DIPLOMATIC_OFFICIAL':
      return 'Diplomatic/Official(A-1,A-2)';
    case 'VISA_EXEMPTED':
      return 'Visa Exempted(B)';
    case 'JOURNALISM':
      return 'Journalism/Religious Affairs(C-1, D-5, D-6)';
    case 'SHORT_TEM_VISIT':
      return 'Short Term Visit(C-2, C-3)';
    case 'STUDY':
      return 'Study(D-2)';
    case 'LANGUAGE_STUDY':
      return 'Language Study(D-4)';
    case 'TECHNICAL_TRAINEE':
      return 'Technical Trainee(D-3)';
    // 과거 계약 기록에만 남은 레거시 값
    case 'TRAINEE':
      return 'Trainee(D-3,D-4)';
    case 'INTRA_COMPANY_TRANSFER':
      return 'Intra-Company Transfer(D-7)';
    case 'PROFESSIONAL':
      return 'Professional(C-4, D-1, D-8, D-9, D-10, E-1, E-2, E-3, E-4, E-5, E-6, E-7)';
    case 'NON_PROFESSIONAL':
      return 'Non-Professional(E-8, E-9, E-10)';
    case 'WORKING_HOLIDAY':
      return 'Working Holiday(H-1)';
    case 'WORK_VISIT':
      return 'Work and Visit(H-2)';
    case 'FAMILY_VISITOR':
      return 'Family Visitor/Dependent Family(F-1, F-2, F-3)';
    case 'OVERSEA_KOREAN':
      return 'Overseas Korean(F-4)';
    case 'PERMANENT_RESIDENCE':
      return 'Permanent Residence(F-5)';
    case 'MARRIAGE_MIGRANT':
      return 'Marriage Migrant(F-6)';
    case 'OTHERS':
      return 'Others(G-1)';
    default:
      return 'null';
  }
};

/**
 *
 * @param whereType 서버에서 받는 whereType
 * @returns 유저가 보는 whereType
 */
export const convertRequestToWhereType = (whereType: string): string | undefined => {
  switch (whereType) {
    case 'UNIVERSITY':
      return 'University';
    case 'ADDRESS':
      return 'Address';
    case 'STATION':
      return 'Station';
    default:
      return undefined;
  }
};

export const convertRoomTypeToRequest = (roomType: string): roomType => {
  switch (roomType) {
    case 'Studio':
      return 'STUDIO';
    case 'Sharehouse':
      return 'SHAREHOUSE';
    case 'Officetel':
      return 'OFFICETEL';
    case 'Others':
      return 'OTHERS';
    case 'Goshiwon':
      return 'GOSHIWON';
    default:
      return 'OTHERS';
  }
};

export const convertRequestToRoomType = (roomType: string): string => {
  switch (roomType) {
    case 'STUDIO':
      return 'Studio';
    case 'SHAREHOUSE':
      return 'Sharehouse';
    case 'OFFICETEL':
      return 'Officetel';
    case 'OTHERS':
      return 'Others';
    case 'GOSHIWON':
      return 'Goshiwon';
    default:
      return 'Others';
  }
};

export const convertRequestToMonth = (month: string): string => {
  switch (month) {
    case '01':
      return 'JAN';
    case '02':
      return 'FEB';
    case '03':
      return 'MAR';
    case '04':
      return 'APR';
    case '05':
      return 'MAY';
    case '06':
      return 'JUN';
    case '07':
      return 'JUL';
    case '08':
      return 'AUG';
    case '09':
      return 'SEP';
    case '10':
      return 'OCT';
    case '11':
      return 'NOV';
    case '12':
      return 'DEC';
    default:
      return 'null';
  }
};

export const convertMonthToRequest = (month: string): string => {
  switch (month) {
    case 'JAN':
      return '01';
    case 'FEB':
      return '02';
    case 'MAR':
      return '03';
    case 'APR':
      return '04';
    case 'MAY':
      return '05';
    case 'JUN':
      return '06';
    case 'JUL':
      return '07';
    case 'AUG':
      return '08';
    case 'SEP':
      return '09';
    case 'OCT':
      return '10';
    case 'NOV':
      return '11';
    case 'DEC':
      return '12';
    default:
      return 'null';
  }
};

export const convertNumToCountryCode = (num: string): string => {
  return COUNTRY_CODE_OPTIONS.find((option) => removeWhitespace(option, 'NUM') === num) || '';
};

export const convertRequestToPaymentType = (paymentType: string, currentLanguage?: string): string => {
  if (!currentLanguage) {
    // Fallback to English if no language specified
    switch (paymentType) {
      case 'MONTHLY':
        return 'Monthly Payment';
      case 'LUMP_SUM':
        return 'Lump-sum Payment';
      default:
        return '-';
    }
  }

  switch (currentLanguage) {
    case 'vi':
      switch (paymentType) {
        case 'MONTHLY':
          return 'Thanh toán hàng tháng';
        case 'LUMP_SUM':
          return 'Thanh toán một lần';
        default:
          return '-';
      }
    case 'zh':
      switch (paymentType) {
        case 'MONTHLY':
          return '按月支付';
        case 'LUMP_SUM':
          return '一次性支付';
        default:
          return '-';
      }
    case 'ja':
      switch (paymentType) {
        case 'MONTHLY':
          return '月払い';
        case 'LUMP_SUM':
          return '一括払い';
        default:
          return '-';
      }
    default: // 'en' or any other language
      switch (paymentType) {
        case 'MONTHLY':
          return 'Monthly Payment';
        case 'LUMP_SUM':
          return 'Lump-sum Payment';
        default:
          return '-';
      }
  }
};

export const convertRequestToFacilityType = (facilityType: string): string => {
  switch (facilityType) {
    case 'ELEVATOR':
      return 'Elevator';
    case 'CCTV':
      return 'CCTV';
    case 'ENTRY_DOORLOCK':
      return 'Entry Doorlock';
    default:
      return '-';
  }
};

export const convertRequestToRentExcludesType = (rentExcludesType: string, currentLanguage?: string): string => {
  // 언어별 텍스트 매핑
  const getLocalizedText = (type: string, language?: string): string => {
    switch (type) {
      case 'ELECTRICITY':
        switch (language) {
          case 'vi':
            return 'Điện';
          case 'zh':
            return '电';
          case 'en':
          default:
            return 'Electricity';
        }
      case 'GAS':
        switch (language) {
          case 'vi':
            return 'Gas';
          case 'zh':
            return '燃气';
          case 'en':
          default:
            return 'Gas';
        }
      case 'WATER':
        switch (language) {
          case 'vi':
            return 'Nước';
          case 'zh':
            return '水';
          case 'en':
          default:
            return 'Water';
        }
      case 'INTERNET':
        switch (language) {
          case 'vi':
            return 'Internet';
          case 'zh':
            return '网络';
          case 'en':
          default:
            return 'Internet';
        }
      default:
        return '-';
    }
  };

  return getLocalizedText(rentExcludesType, currentLanguage);
};

export const convertRentExcludesList = (rentExcludesList: string[], currentLanguage?: string): string => {
  if (rentExcludesList.length === 0) {
    return '-';
  }

  console.log('rentExcludesList', rentExcludesList);
  console.log('currentLanguage', currentLanguage);

  return rentExcludesList
    .map((rentExcludes) => convertRequestToRentExcludesType(rentExcludes, currentLanguage))
    .join(', ');
};

export const convertRequestToCommentType = (commentType: string, currentLanguage?: string): string => {
  if (!currentLanguage) {
    // Fallback to English if no language specified
    switch (commentType) {
      case 'COMMENT1':
        return 'Only women can move in.';
      case 'COMMENT11':
        return 'Only men can move in.';
      case 'COMMENT2':
        return 'It is close to the subway station.';
      case 'COMMENT3':
        return "It's close to the university shuttle bus stop.";
      case 'COMMENT4':
        return 'It has good view.';
      case 'COMMENT5':
        return 'It has good natural lighting.';
      case 'COMMENT6':
        return 'The landlord takes good care of it.';
      case 'COMMENT7':
        return "There are parks and trails nearby, so it's great for a walk.";
      case 'COMMENT8':
        return "It's a newly built building.";
      case 'COMMENT9':
        return 'The cleaning may not be perfect, cleaning before moving in is recommended.';
      case 'COMMENT10':
        return "It's a semi-basement unit, but from the outside, it appears to be on the first floor.";
      default:
        return '-';
    }
  }

  switch (currentLanguage) {
    case 'vi':
      switch (commentType) {
        case 'COMMENT1':
          return 'Chỉ dành cho nữ.';
        case 'COMMENT11':
          return 'Chỉ dành cho nam.';
        case 'COMMENT2':
          return 'Gần ga tàu điện ngầm.';
        case 'COMMENT3':
          return 'Gần trạm xe buýt đưa đón của trường đại học.';
        case 'COMMENT4':
          return 'Tầm nhìn đẹp.';
        case 'COMMENT5':
          return 'Ánh sáng tốt.';
        case 'COMMENT6':
          return 'Chủ nhà quản lý nhà tốt.';
        case 'COMMENT7':
          return 'Gần công viên, đường tản bộ nên có thể đi dạo.';
        case 'COMMENT8':
          return 'Nhà mới xây.';
        case 'COMMENT9':
          return 'Nhà chưa được dọn dẹp vệ sinh hoàn chỉnh nên cần vệ sinh lại trước khi vào ở.';
        case 'COMMENT10':
          return 'Nhà bán hầm nhưng nhìn từ ngoài vào thật ra là tầng 1.';
        default:
          return '-';
      }
    case 'zh':
      switch (commentType) {
        case 'COMMENT1':
          return '仅限女性入住。';
        case 'COMMENT11':
          return '仅限男性入住。';
        case 'COMMENT2':
          return '靠近地铁站。';
        case 'COMMENT3':
          return '靠近大学班车站。';
        case 'COMMENT4':
          return '视野佳。';
        case 'COMMENT5':
          return '采光佳。';
        case 'COMMENT6':
          return '房东管理到位。';
        case 'COMMENT7':
          return '附近有公园和步道，适合散步。';
        case 'COMMENT8':
          return '全新建筑。';
        case 'COMMENT9':
          return '清洁状态不够完善，建议入住前自行打扫。';
        case 'COMMENT10':
          return '虽然是半地下，但是从外面看是一楼。';
        default:
          return '-';
      }
    case 'ja':
      switch (commentType) {
        case 'COMMENT1':
          return '女性専用です。';
        case 'COMMENT11':
          return '男性専用です。';
        case 'COMMENT2':
          return '地下鉄の駅から近いです。';
        case 'COMMENT3':
          return '大学のシャトルバス乗り場から近いです。';
        case 'COMMENT4':
          return '眺めが良いお部屋です。';
        case 'COMMENT5':
          return '日当たりが良好です。';
        case 'COMMENT6':
          return '大家さんが丁寧に管理しています。';
        case 'COMMENT7':
          return '近くに公園や散歩コースがあり、散歩に最適です。';
        case 'COMMENT8':
          return '新築の建物です。';
        case 'COMMENT9':
          return '清掃が十分でない場合があるため、入居前に清掃されることをおすすめします。';
        case 'COMMENT10':
          return '半地下のお部屋ですが、外からは1階のように見えます。';
        default:
          return '-';
      }
    default: // 'en' or any other language
      switch (commentType) {
        case 'COMMENT1':
          return 'Only women can move in.';
        case 'COMMENT11':
          return 'Only men can move in.';
        case 'COMMENT2':
          return 'It is close to the subway station.';
        case 'COMMENT3':
          return "It's close to the university shuttle bus stop.";
        case 'COMMENT4':
          return 'It has good view.';
        case 'COMMENT5':
          return 'It has good natural lighting.';
        case 'COMMENT6':
          return 'The landlord takes good care of it.';
        case 'COMMENT7':
          return "There are parks and trails nearby, so it's great for a walk.";
        case 'COMMENT8':
          return "It's a newly built building.";
        case 'COMMENT9':
          return 'The cleaning may not be perfect, cleaning before moving in is recommended.';
        case 'COMMENT10':
          return "It's a semi-basement unit, but from the outside, it appears to be on the first floor.";
        default:
          return '-';
      }
  }
};

export const convertSubwayStationKorToEng = (subway: string): string => {
  if (stationData.hasOwnProperty(subway)) {
    return stationData[subway as keyof typeof stationData];
  }

  return subway;
};

export const convertSubwayStationEngToKor = (subway: string): string => {
  const station = Object.entries(stationData).find(([, value]) => value === subway);

  return station ? station[0] : subway;
};

export const convertRequestToLanguage = (language: string): string => {
  switch (language) {
    case 'ENGLISH':
      return 'English';
    case 'KOREAN':
      return 'Korean';
    case 'CHINESE':
      return 'Chinese';
    case 'JAPANESE':
      return 'Japanese';
    case 'VIETNAMESE':
      return 'Vietnamese';
    default:
      return '-';
  }
};

export const convertRequestToUniversity = (univ: string, t?: TFunction): string => {
  if (t) {
    switch (univ) {
      case 'SNU':
        return t('university.SNU');
      case 'SSU':
        return t('university.SSU');
      case 'CAU':
        return t('university.CAU');
      case 'SKKU':
        return t('university.SKKU');
      case 'KOREA':
        return t('university.KOREA');
      case 'DONGGUK':
        return t('university.DONGGUK');
      case 'EWHA':
        return t('university.EWHA');
      case 'KHU':
        return t('university.KHU');
      case 'HUFS':
        return t('university.HUFS');
      case 'KONKUK':
        return t('university.KONKUK');
      case 'HONGIK':
        return t('university.HONGIK');
      case 'YONSEI':
        return t('university.YONSEI');
      case 'SOGANG':
        return t('university.SOGANG');
      case 'UOS':
        return t('university.UOS');
      case 'HANSUNG':
        return t('university.HANSUNG');
      case 'SEJONG':
        return t('university.SEJONG');
      case 'SUNGSHIN_WOMEN':
        return t('university.SUNGSHIN_WOMEN');
      case 'GACHON':
        return t('university.GACHON');
      case 'HANYANG_ERICA':
        return t('university.HANYANG_ERICA');
      case 'INHA':
        return t('university.INHA');
      case 'AJOU':
        return t('university.AJOU');
      case 'KHU_GLOBAL':
        return t('university.KHU_GLOBAL');
      case 'SKKU_SUWON':
        return t('university.SKKU_SUWON');
      case 'HUFS_GLOBAL':
        return t('university.HUFS_GLOBAL');
      case 'MYONGJI_YONGIN':
        return t('university.MYONGJI_YONGIN');
      case 'ANSAN':
        return t('university.ANSAN');
      case 'BUCHEON':
        return t('university.BUCHEON');
      case 'SNU_YEONGEON':
        return t('university.SNU_YEONGEON');
      case 'CAU_ANSEONG':
        return t('university.CAU_ANSEONG');
      case 'CATHOLIC_KOREA_BUCHEON':
        return t('university.CATHOLIC_KOREA_BUCHEON');
      case 'KAIST_DAEJEON':
        return t('university.KAIST_DAEJEON');
      case 'CNU':
        return t('university.CNU');
      case 'HANBAT':
        return t('university.HANBAT');
      case 'WOOSONG':
        return t('university.WOOSONG');
      case 'PAI_CHAI':
        return t('university.PAI_CHAI');
      case 'MOKWON':
        return t('university.MOKWON');
      case 'DAEJEON':
        return t('university.DAEJEON');
      case 'EULJI_DAEJEON':
        return t('university.EULJI_DAEJEON');
      default:
        return '-';
    }
  }

  // Fallback to English if no t function provided
  switch (univ) {
    case 'SNU':
      return 'Seoul National University';
    case 'SSU':
      return 'Soongsil University';
    case 'CAU':
      return 'Chung-Ang University';
    case 'SKKU':
      return 'Sungkyunkwan University';
    case 'KOREA':
      return 'Korea University';
    case 'DONGGUK':
      return 'Dongguk University';
    case 'EWHA':
      return 'Ewha University';
    case 'KHU':
      return 'Kyung Hee University';
    case 'HUFS':
      return 'Hankuk University of Foreign Studies';
    case 'KONKUK':
      return 'Konkuk University';
    case 'HONGIK':
      return 'Hongik University';
    case 'YONSEI':
      return 'Yonsei University';
    case 'SOGANG':
      return 'Sogang University';
    case 'UOS':
      return 'University of Seoul';
    case 'HANSUNG':
      return 'Hansung University';
    case 'SEJONG':
      return 'Sejong University';
    case 'SUNGSHIN_WOMEN':
      return "Sungshin Women's University";
    case 'GACHON':
      return 'Gachon University';
    case 'HANYANG_ERICA':
      return 'Hanyang University ERICA Campus';
    case 'INHA':
      return 'Inha University';
    case 'AJOU':
      return 'Ajou University';
    case 'KHU_GLOBAL':
      return 'Kyung Hee University Global Campus(Yongin)';
    case 'SKKU_SUWON':
      return 'Sungkyunkwan University Natural Sciences Campus(Suwon)';
    case 'HUFS_GLOBAL':
      return 'Hankuk University of Foreign Studies Global Campus(Yongin)';
    case 'MYONGJI_YONGIN':
      return 'Myongji University Natural Sciences Campus(Yongin)';
    case 'ANSAN':
      return 'Ansan University';
    case 'BUCHEON':
      return 'Bucheon University';
    case 'SNU_YEONGEON':
      return 'Seoul National University Yeongeon Campus';
    case 'CAU_ANSEONG':
      return 'Chung-Ang University Anseong Campus';
    case 'CATHOLIC_KOREA_BUCHEON':
      return 'Catholic University of Korea Songsim Global Campus (Bucheon)';
    case 'KAIST_DAEJEON':
      return 'KAIST Daejeon Campus';
    case 'CNU':
      return 'Chungnam National University';
    case 'HANBAT':
      return 'Hanbat National University';
    case 'WOOSONG':
      return 'Woosong University';
    case 'PAI_CHAI':
      return 'Pai Chai University';
    case 'MOKWON':
      return 'Mokwon University';
    case 'DAEJEON':
      return 'Daejeon University';
    case 'EULJI_DAEJEON':
      return 'Eulji University Daejeon Campus';
    default:
      return '-';
  }
};

export const convertRequestToUniversityKorean = (univ: string): string => {
  switch (univ) {
    case 'SNU':
      return '서울대학교';
    case 'SSU':
      return '숭실대학교';
    case 'CAU':
      return '중앙대학교';
    case 'SKKU':
      return '성균관대학교';
    case 'KOREA':
      return '고려대학교';
    case 'DONGGUK':
      return '동국대학교';
    case 'EWHA':
      return '이화여자대학교';
    case 'KHU':
      return '경희대학교';
    case 'HUFS':
      return '한국외국어대학교';
    case 'KONKUK':
      return '건국대학교';
    case 'HONGIK':
      return '홍익대학교';
    case 'YONSEI':
      return '연세대학교';
    case 'SOGANG':
      return '서강대학교';
    case 'UOS':
      return '서울시립대학교';
    case 'HANSUNG':
      return '한성대학교';
    case 'SEJONG':
      return '세종대학교';
    case 'SUNGSHIN_WOMEN':
      return '성신여자대학교';
    case 'GACHON':
      return '가천대학교';
    case 'HANYANG_ERICA':
      return '한양대학교ERICA';
    case 'INHA':
      return '인하대학교';
    case 'AJOU':
      return '아주대학교';
    case 'KHU_GLOBAL':
      return '경희대학교 국제캠퍼스';
    case 'SKKU_SUWON':
      return '성균관대학교 자연과학캠퍼스(수원)';
    case 'HUFS_GLOBAL':
      return '한국외국어대학교 글로벌캠퍼스(용인)';
    case 'MYONGJI_YONGIN':
      return '명지대학교 자연캠퍼스(용인)';
    case 'ANSAN':
      return '안산대학교';
    case 'BUCHEON':
      return '부천대학교';
    case 'SNU_YEONGEON':
      return '서울대학교 연건캠퍼스';
    case 'CAU_ANSEONG':
      return '중앙대학교 안성캠퍼스';
    case 'CATHOLIC_KOREA_BUCHEON':
      return '가톨릭대학교 성심교정(부천)';
    case 'KAIST_DAEJEON':
      return 'KAIST 대전캠퍼스';
    case 'CNU':
      return '충남대학교';
    case 'HANBAT':
      return '한밭대학교';
    case 'WOOSONG':
      return '우송대학교';
    case 'PAI_CHAI':
      return '배재대학교';
    case 'MOKWON':
      return '목원대학교';
    case 'DAEJEON':
      return '대전대학교';
    case 'EULJI_DAEJEON':
      return '을지대학교 대전캠퍼스';
    default:
      return '-';
  }
};

export const convertNationalityToShortCode = (nationality: string): string => {
  switch (nationality) {
    case 'Korea (+82)':
      return 'KR';
    case 'Vietnam (+84)':
      return 'VN';
    case 'China (+86)':
      return 'CN';
    case 'USA, Canada (+1)':
      return 'US';
    case 'Germany (+49)':
      return 'DE';
    case 'Afghanistan (+93)':
      return 'AF';
    case 'Albania (+355)':
      return 'AL';
    case 'Algeria (+213)':
      return 'DZ';
    case 'American Samoa (+1)':
      return 'AS';
    case 'Andorra (+376)':
      return 'AD';
    case 'Angola (+244)':
      return 'AO';
    case 'Anguilla (+1)':
      return 'AI';
    case 'Antigua and Barbuda (+1)':
      return 'AG';
    case 'Argentina (+54)':
      return 'AR';
    case 'Armenia (+374)':
      return 'AM';
    case 'Australia (+61)':
      return 'AU';
    case 'Austria (+43)':
      return 'AT';
    case 'Azerbaijan (+994)':
      return 'AZ';
    case 'Bahamas (+1)':
      return 'BS';
    case 'Bahrain (+973)':
      return 'BH';
    case 'Bangladesh (+880)':
      return 'BD';
    case 'Barbados (+1)':
      return 'BB';
    case 'Belarus (+375)':
      return 'BY';
    case 'Belgium (+32)':
      return 'BE';
    case 'Belize (+501)':
      return 'BZ';
    case 'Benin (+229)':
      return 'BJ';
    case 'Bermuda (+1)':
      return 'BM';
    case 'Bhutan (+975)':
      return 'BT';
    case 'Bolivia (+591)':
      return 'BO';
    case 'Bosnia and Herzegovina (+387)':
      return 'BA';
    case 'Botswana (+267)':
      return 'BW';
    case 'Brazil (+55)':
      return 'BR';
    case 'Brunei (+673)':
      return 'BN';
    case 'Bulgaria (+359)':
      return 'BG';
    case 'Burkina Faso (+226)':
      return 'BF';
    case 'Burundi (+257)':
      return 'BI';
    case 'Cambodia (+855)':
      return 'KH';
    case 'Cameroon (+237)':
      return 'CM';
    case 'Canada (+1)':
      return 'CA';
    case 'Cape Verde (+238)':
      return 'CV';
    case 'Cayman Islands (+1)':
      return 'KY';
    case 'Central African Republic (+236)':
      return 'CF';
    case 'Chad (+235)':
      return 'TD';
    case 'Chile (+56)':
      return 'CL';
    case 'Colombia (+57)':
      return 'CO';
    case 'Comoros (+269)':
      return 'KM';
    case 'Congo (+242)':
      return 'CG';
    case 'Congo, Democratic Republic (+243)':
      return 'CD';
    case 'Costa Rica (+506)':
      return 'CR';
    case 'Croatia (+385)':
      return 'HR';
    case 'Cuba (+53)':
      return 'CU';
    case 'Cyprus (+357)':
      return 'CY';
    case 'Czech Republic (+420)':
      return 'CZ';
    case 'Denmark (+45)':
      return 'DK';
    case 'Djibouti (+253)':
      return 'DJ';
    case 'Dominica (+1)':
      return 'DM';
    case 'Dominican Republic (+1)':
      return 'DO';
    case 'Ecuador (+593)':
      return 'EC';
    case 'Egypt (+20)':
      return 'EG';
    case 'El Salvador (+503)':
      return 'SV';
    case 'Equatorial Guinea (+240)':
      return 'GQ';
    case 'Eritrea (+291)':
      return 'ER';
    case 'Estonia (+372)':
      return 'EE';
    case 'Ethiopia (+251)':
      return 'ET';
    case 'Fiji (+679)':
      return 'FJ';
    case 'Finland (+358)':
      return 'FI';
    case 'France (+33)':
      return 'FR';
    case 'French Guiana (+594)':
      return 'GF';
    case 'Gabon (+241)':
      return 'GA';
    case 'Gambia (+220)':
      return 'GM';
    case 'Georgia (+995)':
      return 'GE';
    case 'Ghana (+233)':
      return 'GH';
    case 'Greece (+30)':
      return 'GR';
    case 'Greenland (+299)':
      return 'GL';
    case 'Grenada (+1)':
      return 'GD';
    case 'Guatemala (+502)':
      return 'GT';
    case 'Guinea (+224)':
      return 'GN';
    case 'Guinea-Bissau (+245)':
      return 'GW';
    case 'Guyana (+592)':
      return 'GY';
    case 'Haiti (+509)':
      return 'HT';
    case 'Honduras (+504)':
      return 'HN';
    case 'Hong Kong (+852)':
      return 'HK';
    case 'Hungary (+36)':
      return 'HU';
    case 'Iceland (+354)':
      return 'IS';
    case 'India (+91)':
      return 'IN';
    case 'Indonesia (+62)':
      return 'ID';
    case 'Iran (+98)':
      return 'IR';
    case 'Iraq (+964)':
      return 'IQ';
    case 'Ireland (+353)':
      return 'IE';
    case 'Israel (+972)':
      return 'IL';
    case 'Italy (+39)':
      return 'IT';
    case 'Jamaica (+1)':
      return 'JM';
    case 'Japan (+81)':
      return 'JP';
    case 'Jordan (+962)':
      return 'JO';
    case 'Kazakhstan (+7)':
      return 'KZ';
    case 'Kenya (+254)':
      return 'KE';
    case 'Kiribati (+686)':
      return 'KI';
    case 'Kuwait (+965)':
      return 'KW';
    case 'Kyrgyzstan (+996)':
      return 'KG';
    case 'Laos (+856)':
      return 'LA';
    case 'Latvia (+371)':
      return 'LV';
    case 'Lebanon (+961)':
      return 'LB';
    case 'Lesotho (+266)':
      return 'LS';
    case 'Liberia (+231)':
      return 'LR';
    case 'Libya (+218)':
      return 'LY';
    case 'Liechtenstein (+423)':
      return 'LI';
    case 'Lithuania (+370)':
      return 'LT';
    case 'Luxembourg (+352)':
      return 'LU';
    case 'Macao (+853)':
      return 'MO';
    case 'Macedonia (+389)':
      return 'MK';
    case 'Madagascar (+261)':
      return 'MG';
    case 'Malawi (+265)':
      return 'MW';
    case 'Malaysia (+60)':
      return 'MY';
    case 'Maldives (+960)':
      return 'MV';
    case 'Mali (+223)':
      return 'ML';
    case 'Malta (+356)':
      return 'MT';
    case 'Marshall Islands (+692)':
      return 'MH';
    case 'Mauritania (+222)':
      return 'MR';
    case 'Mauritius (+230)':
      return 'MU';
    case 'Mexico (+52)':
      return 'MX';
    case 'Micronesia (+691)':
      return 'FM';
    case 'Moldova (+373)':
      return 'MD';
    case 'Monaco (+377)':
      return 'MC';
    case 'Mongolia (+976)':
      return 'MN';
    case 'Montenegro (+382)':
      return 'ME';
    case 'Morocco (+212)':
      return 'MA';
    case 'Mozambique (+258)':
      return 'MZ';
    case 'Myanmar (+95)':
      return 'MM';
    case 'Namibia (+264)':
      return 'NA';
    case 'Nauru (+674)':
      return 'NR';
    case 'Nepal (+977)':
      return 'NP';
    case 'Netherlands (+31)':
      return 'NL';
    case 'New Zealand (+64)':
      return 'NZ';
    case 'Nicaragua (+505)':
      return 'NI';
    case 'Niger (+227)':
      return 'NE';
    case 'Nigeria (+234)':
      return 'NG';
    case 'North Korea (+850)':
      return 'KP';
    case 'Norway (+47)':
      return 'NO';
    case 'Oman (+968)':
      return 'OM';
    case 'Pakistan (+92)':
      return 'PK';
    case 'Palau (+680)':
      return 'PW';
    case 'Palestine (+970)':
      return 'PS';
    case 'Panama (+507)':
      return 'PA';
    case 'Papua New Guinea (+675)':
      return 'PG';
    case 'Paraguay (+595)':
      return 'PY';
    case 'Peru (+51)':
      return 'PE';
    case 'Philippines (+63)':
      return 'PH';
    case 'Poland (+48)':
      return 'PL';
    case 'Portugal (+351)':
      return 'PT';
    case 'Qatar (+974)':
      return 'QA';
    case 'Romania (+40)':
      return 'RO';
    case 'Russia (+7)':
      return 'RU';
    case 'Rwanda (+250)':
      return 'RW';
    case 'Saint Kitts and Nevis (+1)':
      return 'KN';
    case 'Saint Lucia (+1)':
      return 'LC';
    case 'Saint Vincent and the Grenadines (+1)':
      return 'VC';
    case 'Samoa (+685)':
      return 'WS';
    case 'San Marino (+378)':
      return 'SM';
    case 'Sao Tome and Principe (+239)':
      return 'ST';
    case 'Saudi Arabia (+966)':
      return 'SA';
    case 'Senegal (+221)':
      return 'SN';
    case 'Serbia (+381)':
      return 'RS';
    case 'Seychelles (+248)':
      return 'SC';
    case 'Sierra Leone (+232)':
      return 'SL';
    case 'Singapore (+65)':
      return 'SG';
    case 'Slovakia (+421)':
      return 'SK';
    case 'Slovenia (+386)':
      return 'SI';
    case 'Solomon Islands (+677)':
      return 'SB';
    case 'Somalia (+252)':
      return 'SO';
    case 'South Africa (+27)':
      return 'ZA';
    case 'South Sudan (+211)':
      return 'SS';
    case 'Spain (+34)':
      return 'ES';
    case 'Sri Lanka (+94)':
      return 'LK';
    case 'Sudan (+249)':
      return 'SD';
    case 'Suriname (+597)':
      return 'SR';
    case 'Swaziland (+268)':
      return 'SZ';
    case 'Sweden (+46)':
      return 'SE';
    case 'Switzerland (+41)':
      return 'CH';
    case 'Syria (+963)':
      return 'SY';
    case 'Taiwan (+886)':
      return 'TW';
    case 'Tajikistan (+992)':
      return 'TJ';
    case 'Tanzania (+255)':
      return 'TZ';
    case 'Thailand (+66)':
      return 'TH';
    case 'Timor-Leste (+670)':
      return 'TL';
    case 'Togo (+228)':
      return 'TG';
    case 'Tonga (+676)':
      return 'TO';
    case 'Trinidad and Tobago (+1)':
      return 'TT';
    case 'Tunisia (+216)':
      return 'TN';
    case 'Turkey (+90)':
      return 'TR';
    case 'Turkmenistan (+993)':
      return 'TM';
    case 'Tuvalu (+688)':
      return 'TV';
    case 'Uganda (+256)':
      return 'UG';
    case 'Ukraine (+380)':
      return 'UA';
    case 'United Arab Emirates (+971)':
      return 'AE';
    case 'United Kingdom (+44)':
      return 'GB';
    case 'Uruguay (+598)':
      return 'UY';
    case 'Uzbekistan (+998)':
      return 'UZ';
    case 'Vanuatu (+678)':
      return 'VU';
    case 'Venezuela (+58)':
      return 'VE';
    case 'Yemen (+967)':
      return 'YE';
    case 'Zambia (+260)':
      return 'ZM';
    case 'Zimbabwe (+263)':
      return 'ZW';
    default:
      return '';
  }
};

/**
 * @description
 * options 데이터에서 날짜와 시간을 추출하여 GA4 이벤트 형식으로 변환합니다.
 *
 * @param options - 날짜와 시간 슬롯이 포함된 옵션 배열
 * @returns GA4 이벤트에 사용할 수 있는 날짜 형식 객체
 *
 * @example
 * ```typescript
 * const options = [
 *   { date: "2025.07.30", timeSlots: ['11:00', '12:00'] }
 * ];
 *
 * const result = getFormattedDates(options);
 * // 결과: { main_contract_date1: "2025-07-30 11:00", main_contract_date2: "2025-07-30 12:00" }
 * ```
 */
export const getFormattedDates = (options: Array<{ date: string; timeSlots: string[] }>): Record<string, string> => {
  const formattedDates: Record<string, string> = {};
  let dateIndex = 1;

  options.forEach((option) => {
    if (option.date && option.timeSlots.length > 0) {
      const kstDateTime = dayjs(option.date);
      const formattedDate = kstDateTime.format('YYYY-MM-DD');

      option.timeSlots.forEach((timeSlot) => {
        const [hours, minutes] = timeSlot.split(':').map(Number);
        const kstTime = dayjs().hour(hours).minute(minutes).format('HH:mm');

        formattedDates[`main_contract_date${dateIndex}`] = `${formattedDate} ${kstTime}`;
        dateIndex++;
      });
    }
  });

  return formattedDates;
};

/**
 * @description
 * URL에서 언어 코드를 제거하고 실제 경로를 반환합니다.
 * 허용된 언어 코드 목록에 있는 경우에만 언어 코드를 제거합니다.
 *
 * @param pathname - 현재 경로
 * @returns 언어 코드가 제거된 경로 또는 원본 경로
 *
 * @example
 * getPathWithoutLang('/vi/my/account') // → '/my/account'
 * getPathWithoutLang('/zh/listing')    // → '/listing'
 * getPathWithoutLang('/my/account')    // → '/my/account'
 * getPathWithoutLang('/unknown/home') // → '/unknown/home'
 */
export const getPathWithoutLang = (pathname: string): string => {
  const allowedLangs = ['vi', 'zh', 'ja'];
  const match = pathname.match(/^\/([a-z]{2})(\/.*)?$/i);

  if (match && allowedLangs.includes(match[1].toLowerCase())) {
    return match[2] || '/';
  }
  return pathname;
};

/** 사이트 언어(i18n) → 서버 선호 언어 코드 */
export const getPreferredLanguageCode = (language: string): string => {
  if (language.startsWith('zh')) return 'SIMPLIFIED_CHINESE';
  if (language.startsWith('vi')) return 'VIETNAMESE';
  if (language.startsWith('ja')) return 'JAPANESE';
  return 'ENGLISH';
};
